import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiBaseService } from './api.base.service';

export interface LeadAlertSession {
  sessionId: string;
  leadId: number;
  leadName: string;
  leadPhone: string;
  programName?: string;
  campusName?: string;
  status: 'DIALING' | 'ANSWERED' | 'BRIDGED' | 'COMPLETED';
}

export interface LeadAlertResponse {
  success: boolean;
  message?: string;
  isWinner?: boolean;
}

/**
 * Service for Lead Alert API operations
 * Handles accept/decline actions via API instead of DTMF
 */
@Injectable({
  providedIn: 'root'
})
export class LeadAlertService extends ApiBaseService {
  private http = inject(HttpClient);

  constructor() {
    super(inject(HttpClient), '/lead-alert');
  }

  /**
   * Accept lead alert - tells backend agent accepted
   * @param sessionId The lead alert session ID
   * @param callControlId The Telnyx call control ID
   * @returns Observable with response indicating if agent won the race
   */
  acceptLeadAlert(sessionId: string, callControlId: string): Observable<LeadAlertResponse> {
    const url = `${environment.apiUrl}/lead-alert/accept`;
    return this.http.post<LeadAlertResponse>(url, {
      sessionId,
      callControlId
    }).pipe(
      catchError(error => {
        console.error('Failed to accept lead alert:', error);
        throw error;
      })
    );
  }

  /**
   * Decline lead alert - tells backend agent declined
   * @param sessionId The lead alert session ID
   * @param callControlId The Telnyx call control ID
   * @returns Observable with response confirming decline
   */
  declineLeadAlert(sessionId: string, callControlId: string): Observable<LeadAlertResponse> {
    const url = `${environment.apiUrl}/lead-alert/decline`;
    return this.http.post<LeadAlertResponse>(url, {
      sessionId,
      callControlId
    }).pipe(
      catchError(error => {
        console.error('Failed to decline lead alert:', error);
        throw error;
      })
    );
  }

  /**
   * Get lead alert session info
   * @param sessionId The lead alert session ID
   * @returns Observable with session information
   */
  getSessionInfo(sessionId: string): Observable<LeadAlertSession> {
    const url = `${environment.apiUrl}/lead-alert/session/${sessionId}`;
    return this.http.get<{ data: LeadAlertSession }>(url).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Failed to get session info:', error);
        throw error;
      })
    );
  }
}
